import * as React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Button,
  DatePicker,
  FieldHint,
  Input,
  Label,
  Textarea,
  useToast,
} from "@repo/ui";
import { formatToApiDate } from "@repo/utils";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  Hash,
  Package,
  PackageMinus,
  ShieldX,
  StickyNote,
  UserCircle,
  X,
} from "lucide-react";
import { SidePanel } from "@/components/side-panel";
import {
  InventorySearchCombobox,
  PatientCombobox,
  PatientManagementModal,
} from "@/components/forms";
import { PasswordConfirmationModal } from "@/components/security/password-confirmation-modal";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";
import { auditInventory } from "@/lib/api/inventory";
import { ApiClientError } from "@/lib/api/client";
import {
  AuditCountForm,
  type AuditFormData,
} from "@/components/inventory/audit-count-form";
import type { DispenseFormData } from "@/lib/types/inventory";
import type { Patient } from "@/lib/types/patients";
import type { InventoryItem } from "@/components/forms";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3005";

const dispenseSchema = z.object({
  date: z.date({ required_error: "Date is required" }),
  time: z
    .string()
    .regex(/^\d{2}:\d{2}$/, "Invalid time format")
    .default(() => {
      const now = new Date();
      return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
    }),
  ndc: z
    .string()
    .min(1, "NDC is required")
    .regex(/^\d{5}-\d{4}-\d{2}$/, "Invalid NDC format (e.g., 12345-6789-01)"),
  itemName: z.string().min(1, "Item name is required"),
  quantityDispensed: z.coerce
    .number()
    .min(1, "Quantity must be greater than 0"),
  rxNumber: z.string().min(1, "RX number is required"),
  note: z.string().max(1000).optional(),
});

type DispenseFormValues = z.infer<typeof dispenseSchema>;

/** Optional context passed when submitting in create mode (e.g. note to create with the dispense). */
export interface DispenseCreateContext {
  note?: string;
  /** Inventory item details — used for confirm-stock-on-dispense flow */
  inventoryItem?: {
    id: string;
    ndc: string;
    itemName: string;
  };
}

export interface DispenseFormPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    data: DispenseFormData,
    createContext?: DispenseCreateContext
  ) => Promise<void> | void;
  onCheckDuplicateRx: (rxNumber: string) => Promise<boolean>;
  onCheckStock: (
    ndc: string,
    quantity: number
  ) => Promise<{
    available: number;
    willTriggerReorder: boolean;
    expiringItems?: {
      lotNumber: string;
      expirationDate: Date;
      quantity: number;
    }[];
  }>;
  initialData?: Partial<DispenseFormValues> & {
    patientId?: string;
    patientName?: string;
  };
  mode?: "create" | "edit";
  /** When true, a stock confirmation step is shown before the dispense is submitted. */
  confirmStockEnabled?: boolean;
  /** Which drug schedules require stock confirmation: CII only or all controlled substances. */
  confirmStockScope?: "cii_only" | "all";
  /** When true, selecting a patient is required before completing a dispense. */
  requirePatientEnabled?: boolean;
}

export function DispenseFormPanel({
  open,
  onOpenChange,
  onSubmit,
  onCheckDuplicateRx,
  onCheckStock,
  initialData,
  mode = "create",
  confirmStockEnabled = false,
  confirmStockScope = "cii_only",
  requirePatientEnabled = false,
}: DispenseFormPanelProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [duplicateRxWarning, setDuplicateRxWarning] = React.useState<
    string | null
  >(null);
  const [stockWarning, setStockWarning] = React.useState<string | null>(null);
  const [expirationWarning, setExpirationWarning] = React.useState<
    string | null
  >(null);
  // Prevent re-initializing the form when parent re-renders cause initialData
  // to get a new reference (inline object). Only init once per open session.
  const hasInitializedRef = React.useRef(false);
  const [confirmDuplicateRx, setConfirmDuplicateRx] = React.useState(false);
  const [permissionError, setPermissionError] = React.useState<string | null>(
    null
  );
  const [selectedInventory, setSelectedInventory] =
    React.useState<InventoryItem | null>(null);
  const [displayLabel, setDisplayLabel] = React.useState<string>("");

  // Patient selection state
  const [linkedPatient, setLinkedPatient] = React.useState<Patient | null>(
    null
  );
  const [showCreatePatient, setShowCreatePatient] = React.useState(false);
  const [createPatientName, setCreatePatientName] = React.useState<
    string | undefined
  >(undefined);
  const [patientsRefreshTrigger, setPatientsRefreshTrigger] = React.useState(0);

  // Confirm stock step state
  type PanelStep = "form" | "confirm-stock";
  const [step, setStep] = React.useState<PanelStep>("form");
  const [pendingDispenseData, setPendingDispenseData] = React.useState<{
    data: DispenseFormData;
    createContext?: DispenseCreateContext;
  } | null>(null);
  const [confirmStockItemData, setConfirmStockItemData] = React.useState<{
    id: string;
    ndc: string;
    itemName: string;
    stockCalculated: number;
  } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = React.useState(false);
  const [pendingAuditData, setPendingAuditData] =
    React.useState<AuditFormData | null>(null);
  const [isAuditLoading, setIsAuditLoading] = React.useState(false);

  // Approval token ref — survives re-renders and panel open/close cycles
  const approvalTokenRef = React.useRef<string | null>(null);
  const formRef = React.useRef<HTMLFormElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<DispenseFormValues>({
    resolver: zodResolver(dispenseSchema),
    defaultValues: initialData || {
      date: new Date(),
      time: (() => {
        const now = new Date();
        return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      })(),
    },
  });

  const date = watch("date");
  const time = watch("time");
  const rxNumber = watch("rxNumber");
  const ndc = watch("ndc");
  const quantityDispensed = watch("quantityDispensed");

  React.useEffect(() => {
    // Reset the initialization flag when the panel closes so it re-inits
    // next time it opens.
    if (!open) {
      hasInitializedRef.current = false;
      return;
    }

    // Only initialize once per open session. Parent re-renders recreate the
    // inline initialData object with a new reference — without this guard the
    // form would reset and overwrite any user edits (date, time, etc.).
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;

    const loadInventoryItem = async () => {
      if (initialData?.ndc && initialData?.itemName) {
        // Clear stale warnings from previous session
        setStockWarning(null);
        setExpirationWarning(null);

        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        reset({
          ...initialData,
          date: initialData.date ?? now,
          time: initialData.time ?? currentTime,
        });

        // If we have NDC and item name, fetch the full inventory item to populate the combobox
        try {
          const response = await fetch(
            `${API_URL}/inventory/search?q=${encodeURIComponent(initialData.ndc)}`,
            { credentials: "include" }
          );
          const data = await response.json();

          if (data.results && data.results.length > 0) {
            // Find the exact match by NDC
            const matchedItem = data.results.find(
              (item: InventoryItem) => item.ndc === initialData.ndc
            );

            if (matchedItem) {
              setSelectedInventory(matchedItem);
              setDisplayLabel(matchedItem.label);
              // Ensure form values are set
              setValue("ndc", matchedItem.ndc, {
                shouldValidate: true,
              });
              setValue("itemName", matchedItem.itemName, {
                shouldValidate: true,
              });
            } else {
              // Fallback: just set the display label without full item
              setDisplayLabel(`${initialData.ndc} - ${initialData.itemName}`);
            }
          } else {
            // Fallback: just set the display label
            setDisplayLabel(`${initialData.ndc} - ${initialData.itemName}`);
          }
        } catch (error) {
          console.error("Failed to fetch inventory item:", error);
          // Fallback: just set the display label
          setDisplayLabel(`${initialData.ndc} - ${initialData.itemName}`);
        }
      } else {
        // Clear stale warnings from previous session
        setStockWarning(null);
        setExpirationWarning(null);

        const now = new Date();
        const currentTime = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        reset({ date: now, time: currentTime });
        setSelectedInventory(null);
        setDisplayLabel("");
      }

      // Pre-populate patient for edit mode
      if (initialData?.patientId && initialData?.patientName) {
        setLinkedPatient({
          id: initialData.patientId,
          first_name: initialData.patientName,
          last_name: null,
          middle_name: null,
          date_of_birth: null,
          phone_number: null,
          street: null,
          street_2: null,
          city: null,
          state: null,
          zip: null,
          pharmacy_id: "",
          dispense_count: 0,
          reminder_count: 0,
          created_at: "",
          updated_at: "",
        } as Patient);
      } else {
        setLinkedPatient(null);
      }
    };

    loadInventoryItem();
  }, [open, initialData, reset, setValue]);

  // User initials no longer stored per-transaction - derived from created_by user join

  const handleInventorySelect = (item: InventoryItem | null) => {
    if (item) {
      setSelectedInventory(item);
      setValue("ndc", item.ndc, { shouldValidate: true });
      setValue("itemName", item.itemName, { shouldValidate: true });
      setDisplayLabel(item.label);
    } else {
      setSelectedInventory(null);
      setValue("ndc", "", { shouldValidate: false });
      setValue("itemName", "", { shouldValidate: false });
      setDisplayLabel("");
    }
  };

  // Check for duplicate RX number
  React.useEffect(() => {
    let cancelled = false;

    const checkDuplicate = async () => {
      if (rxNumber && rxNumber.length > 0 && mode === "create") {
        const isDuplicate = await onCheckDuplicateRx(rxNumber);

        // Only update state if not cancelled
        if (!cancelled) {
          if (isDuplicate) {
            setDuplicateRxWarning(
              `RX #${rxNumber} has been used previously. This is common when multiple NDCs are needed for one prescription.`
            );
          } else {
            setDuplicateRxWarning(null);
            setConfirmDuplicateRx(false);
          }
        }
      }
    };

    const debounce = setTimeout(checkDuplicate, 500);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [rxNumber, mode, onCheckDuplicateRx]);

  // Check stock availability against requested quantity
  React.useEffect(() => {
    let cancelled = false;

    const checkAvailability = async () => {
      if (ndc && quantityDispensed > 0) {
        const stockInfo = await onCheckStock(ndc, quantityDispensed);

        // Only update state if not cancelled
        if (!cancelled) {
          if (stockInfo.available < quantityDispensed) {
            setStockWarning(
              `Insufficient stock! Only ${stockInfo.available} units available. Cannot dispense ${quantityDispensed} units.`
            );
          } else if (stockInfo.willTriggerReorder) {
            setStockWarning(
              `This dispense will trigger low stock status. Stock will be ${stockInfo.available - quantityDispensed} after dispense.`
            );
          } else {
            setStockWarning(null);
          }

          if (stockInfo.expiringItems && stockInfo.expiringItems.length > 0) {
            const item = stockInfo.expiringItems[0];
            setExpirationWarning(
              `Expiring soon: Lot ${item.lotNumber} expires on ${new Date(item.expirationDate).toLocaleDateString()}. ${item.quantity} units available.`
            );
          } else {
            setExpirationWarning(null);
          }
        }
      } else {
        // Clear warnings when check conditions aren't met (e.g. after form reset)
        setStockWarning(null);
        setExpirationWarning(null);
      }
    };

    const debounce = setTimeout(checkAvailability, 500);

    return () => {
      cancelled = true;
      clearTimeout(debounce);
    };
  }, [ndc, quantityDispensed, onCheckStock]);

  function resetFormState() {
    reset();
    setSelectedInventory(null);
    setDisplayLabel("");
    setDuplicateRxWarning(null);
    setConfirmDuplicateRx(false);
    setStockWarning(null);
    setExpirationWarning(null);
    setValue("note", "");
    approvalTokenRef.current = null;
    setLinkedPatient(null);
  }

  function resetConfirmStockState() {
    setStep("form");
    setPendingDispenseData(null);
    setConfirmStockItemData(null);
    setShowPasswordModal(false);
    setPendingAuditData(null);
    setIsAuditLoading(false);
  }

  function handleDispenseError(err: unknown) {
    if (err instanceof ApiClientError && err.statusCode === 403) {
      const errData = err.data as Record<string, unknown>;
      if (errData.requiresApproval) {
        handlePermissionError(err, {
          requestApproval,
          toast,
          retryFn: async (token) => {
            approvalTokenRef.current = token;
            // Re-trigger the form submit with the token now stored in the ref
            formRef.current?.requestSubmit();
          },
          actionDescription:
            mode === "create" ? "Create dispense" : "Update dispense",
        });
      } else {
        setPermissionError(
          "You don't have permission to perform this action. Contact your administrator for access."
        );
      }
      return;
    }

    toast({
      title: "Error",
      description:
        err instanceof ApiClientError
          ? err.message
          : `Failed to ${mode === "create" ? "log" : "update"} dispense. Please try again.`,
      variant: "destructive",
    });
  }

  function handleAuditFormSubmit(data: AuditFormData) {
    setPendingAuditData(data);
    setShowPasswordModal(true);
  }

  async function submitAudit(password: string, approvalToken?: string) {
    if (!pendingAuditData || !confirmStockItemData) return;

    setIsAuditLoading(true);

    try {
      const result = await auditInventory({
        inventoryId: confirmStockItemData.id,
        auditDate: pendingAuditData.auditDate,
        physicalCount: pendingAuditData.physicalCount,
        reason: pendingAuditData.reason,
        justification: pendingAuditData.justification,
        password,
        approvalToken,
      });

      const quantityDifference =
        pendingAuditData.physicalCount - confirmStockItemData.stockCalculated;

      if (result.audit.hasDiscrepancy) {
        toast({
          title: "Stock Verified — Adjustment Recorded",
          description: `${confirmStockItemData.itemName}: physical ${pendingAuditData.physicalCount} (${quantityDifference > 0 ? "+" : ""}${quantityDifference}). Adjustment saved.`,
          variant: "success",
        });
      } else {
        toast({
          title: "Stock Verified — Counts Match",
          description: `${confirmStockItemData.itemName}: physical count of ${pendingAuditData.physicalCount} confirmed.`,
          variant: "success",
        });
      }

      // Audit succeeded — now execute the pending dispense
      setIsAuditLoading(false);
      setShowPasswordModal(false);

      if (pendingDispenseData) {
        const { data, createContext } = pendingDispenseData;
        // Include approval token if present (from a prior approval for the dispense action)
        const token = approvalTokenRef.current;
        approvalTokenRef.current = null;
        const submitData = token
          ? ({ ...data, approvalToken: token } as DispenseFormData)
          : data;
        resetConfirmStockState();
        setIsSubmitting(true);
        try {
          await onSubmit(submitData, createContext);
          resetFormState();
          onOpenChange(false);
        } catch (err) {
          handleDispenseError(err);
        } finally {
          setIsSubmitting(false);
        }
      }
    } catch (err) {
      if (err instanceof ApiClientError && err.statusCode === 403) {
        setShowPasswordModal(false);
        setIsAuditLoading(false);
        handlePermissionError(err, {
          requestApproval,
          toast,
          retryFn: (token) => submitAudit(password, token),
          actionDescription: `Stock verification for ${confirmStockItemData.itemName}`,
        });
        return;
      }

      setIsAuditLoading(false);
      console.error("Stock verification error:", err);
      toast({
        title: "Stock Verification Failed",
        description:
          err instanceof Error ? err.message : "An unexpected error occurred.",
        variant: "destructive",
      });
    }
  }

  async function handlePasswordConfirmed(password: string) {
    await submitAudit(password);
  }

  const handleFormSubmit = async (data: DispenseFormValues) => {
    // Check if patient is required but not provided
    if (requirePatientEnabled && !linkedPatient) {
      toast({
        title: "Patient required",
        description: "Please select a patient before completing the dispense.",
        variant: "destructive",
      });
      return;
    }

    // Check if duplicate RX needs confirmation
    if (duplicateRxWarning && !confirmDuplicateRx) {
      toast({
        title: "Confirmation Required",
        description:
          "Please confirm the duplicate RX number before proceeding.",
        variant: "destructive",
      });
      return;
    }

    setPermissionError(null);

    // Convert Date to YYYY-MM-DD format to avoid timezone issues
    const dateString = data.date ? formatToApiDate(data.date) : "";

    // Combine date + time into dispensed_at ISO string
    const [hours, minutes] = data.time.split(":").map(Number);
    const dispensedAt = new Date(data.date);
    dispensedAt.setHours(hours, minutes, 0, 0);

    // Include approval token if one was obtained from a prior approval flow
    const token = approvalTokenRef.current;
    approvalTokenRef.current = null;

    const patientName = linkedPatient
      ? `${linkedPatient.first_name}${linkedPatient.last_name ? " " + linkedPatient.last_name : ""}`
      : null;

    const dispenseData: Partial<DispenseFormData> & {
      approvalToken?: string;
    } = {
      date: dateString,
      dispensed_at: dispensedAt.toISOString(),
      ndc: data.ndc,
      quantity_dispensed: data.quantityDispensed,
      rx_number: data.rxNumber,
      patient_id: linkedPatient?.id || null,
      patient_name: patientName,
      ...(token ? { approvalToken: token } : {}),
    };

    const createContext: DispenseCreateContext | undefined =
      mode === "create"
        ? {
            ...(data.note?.trim() ? { note: data.note.trim() } : {}),
            ...(selectedInventory
              ? {
                  inventoryItem: {
                    id: selectedInventory.id,
                    ndc: selectedInventory.ndc,
                    itemName: selectedInventory.itemName,
                  },
                }
              : {}),
          }
        : undefined;

    // If confirm stock is enabled and we have an inventory item, transition to confirm step
    // Skip the confirm step on retry (token present means this is an approved re-submit)
    const requiresVerification =
      confirmStockEnabled &&
      selectedInventory &&
      (confirmStockScope === "all" ||
        selectedInventory.drugType === "schedule_ii");

    if (requiresVerification && mode === "create" && !token) {
      try {
        const stockResult = await onCheckStock(selectedInventory.ndc, 1);
        setPendingDispenseData({
          data: dispenseData as DispenseFormData,
          createContext,
        });
        setConfirmStockItemData({
          id: selectedInventory.id,
          ndc: selectedInventory.ndc,
          itemName: selectedInventory.itemName,
          stockCalculated: stockResult.available,
        });
        setStep("confirm-stock");
        return;
      } catch {
        // If stock check fails, proceed with dispense anyway
      }
    }

    // Normal flow — submit directly
    setIsSubmitting(true);
    try {
      await onSubmit(dispenseData as DispenseFormData, createContext);
      resetFormState();
      onOpenChange(false);
    } catch (err) {
      handleDispenseError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  function handlePanelClose() {
    resetFormState();
    resetConfirmStockState();
    onOpenChange(false);
  }

  const isConfirmStep = step === "confirm-stock";

  const panelTitle = isConfirmStep
    ? "Confirm Stock"
    : mode === "create"
      ? "Log New Dispense"
      : "Edit Dispense";

  const panelDescription = isConfirmStep
    ? "Verify physical stock count before completing this dispense"
    : mode === "create"
      ? "Log prescription dispense"
      : "Update dispense information";

  return (
    <>
      <SidePanel
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handlePanelClose();
          else onOpenChange(isOpen);
        }}
        icon={isConfirmStep ? ShieldX : PackageMinus}
        title={panelTitle}
        description={panelDescription}
        footer={
          isConfirmStep ? undefined : (
            <div className="flex gap-3">
              <Button
                type="submit"
                form="dispense-form"
                className="flex-1"
                disabled={isSubmitting}
                data-testid="dispense-form-submit"
              >
                {isSubmitting
                  ? mode === "create"
                    ? "Logging..."
                    : "Updating..."
                  : mode === "create"
                    ? "Log Dispense"
                    : "Update Dispense"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handlePanelClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </div>
          )
        }
      >
        {isConfirmStep && confirmStockItemData ? (
          <div className="space-y-4">
            <button
              type="button"
              onClick={() => {
                resetConfirmStockState();
              }}
              className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to dispense form
            </button>

            <div className="rounded-lg border bg-muted/40 px-4 py-3">
              <p className="text-sm font-medium">
                {confirmStockItemData.itemName}
              </p>
              <p className="text-xs text-muted-foreground">
                NDC: {confirmStockItemData.ndc}
              </p>
            </div>

            <AuditCountForm
              systemCount={confirmStockItemData.stockCalculated}
              onSubmit={handleAuditFormSubmit}
              isLoading={isAuditLoading}
              submitLabel="Confirm & Dispense"
              onCancel={handlePanelClose}
            />

            <PasswordConfirmationModal
              open={showPasswordModal}
              onClose={() => setShowPasswordModal(false)}
              onSuccess={handlePasswordConfirmed}
              title="Confirm Stock Verification"
              description=""
              actionLabel="Verify Stock"
            />
          </div>
        ) : (
          <form
            ref={formRef}
            id="dispense-form"
            onSubmit={handleSubmit(handleFormSubmit)}
            className="space-y-5"
          >
            {/* ── Permission error banner ────────────────────────────── */}
            {permissionError && (
              <div className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
                <ShieldX className="h-4 w-4 shrink-0 mt-0.5 text-destructive" />
                <div>
                  <p className="font-medium text-destructive">
                    Permission Denied
                  </p>
                  <p className="text-destructive/80 mt-0.5 text-xs leading-relaxed">
                    {permissionError}
                  </p>
                </div>
              </div>
            )}

            {/* ── Warnings banner ────────────────────────────────────── */}
            {expirationWarning && (
              <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50/60 px-3.5 py-3 text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-800">
                    Expiration Notice
                  </p>
                  <p className="text-amber-700 mt-0.5 text-xs leading-relaxed">
                    {expirationWarning}
                  </p>
                </div>
              </div>
            )}

            {/* ── Section: When ──────────────────────────────────────── */}
            <fieldset className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Calendar className="h-3.5 w-3.5" />
                When
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="date" className="text-xs">
                    Date
                  </Label>
                  <DatePicker
                    date={date}
                    onDateChange={(date) =>
                      setValue("date", date || new Date())
                    }
                    placeholder="Select date"
                  />
                  {errors.date && (
                    <p className="text-xs text-destructive">
                      {errors.date.message}
                    </p>
                  )}
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="time" className="text-xs">
                    Time
                  </Label>
                  <Input
                    id="time"
                    type="time"
                    value={time ?? ""}
                    onChange={(e) => setValue("time", e.target.value)}
                  />
                  {errors.time && (
                    <p className="text-xs text-destructive">
                      {errors.time.message}
                    </p>
                  )}
                </div>
              </div>
            </fieldset>

            <div className="border-t border-border/50" />

            {/* ── Section: Medication ────────────────────────────────── */}
            <fieldset className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Package className="h-3.5 w-3.5" />
                Medication
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inventory" className="text-xs">
                  Drug
                </Label>
                <InventorySearchCombobox
                  value={selectedInventory?.id}
                  valueItem={selectedInventory}
                  onSelect={handleInventorySelect}
                  placeholder="Search by NDC or item name..."
                  searchPlaceholder="Type to search inventory..."
                  disabled={isSubmitting}
                  displayLabel={displayLabel}
                  autoOpen={mode === "create" && !initialData?.ndc}
                  data-testid="dispense-form-ndc"
                />
                {(errors.ndc || errors.itemName) && (
                  <p className="text-xs text-destructive">
                    {errors.ndc?.message || errors.itemName?.message}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="quantityDispensed" className="text-xs">
                  Quantity Dispensed
                </Label>
                <Input
                  id="quantityDispensed"
                  type="number"
                  placeholder="0"
                  data-testid="dispense-form-qty"
                  {...register("quantityDispensed")}
                />
                {errors.quantityDispensed && (
                  <p className="text-xs text-destructive">
                    {errors.quantityDispensed.message}
                  </p>
                )}
                {stockWarning && !errors.quantityDispensed && (
                  <div
                    className={`flex items-start gap-2 rounded-md px-3 py-2 text-xs ${
                      stockWarning.includes("Insufficient")
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : "bg-amber-50 text-amber-700 border border-amber-200"
                    }`}
                  >
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                    <span className="leading-relaxed">{stockWarning}</span>
                  </div>
                )}
              </div>
            </fieldset>

            <div className="border-t border-border/50" />

            {/* ── Section: Prescription ──────────────────────────────── */}
            <fieldset className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Hash className="h-3.5 w-3.5" />
                Prescription
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="rxNumber" className="text-xs">
                      RX Number
                    </Label>
                    <FieldHint>
                      The prescription number from the patient's Rx. The same
                      number can be used across multiple NDCs when one
                      prescription covers more than one item.
                    </FieldHint>
                  </div>
                  {duplicateRxWarning && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                      <AlertTriangle className="h-3 w-3" />
                      Duplicate
                    </span>
                  )}
                </div>
                <Input
                  id="rxNumber"
                  placeholder="Enter prescription number"
                  className={
                    duplicateRxWarning
                      ? "border-amber-400 focus-visible:ring-amber-400"
                      : ""
                  }
                  data-testid="dispense-form-rx"
                  {...register("rxNumber")}
                />
                {errors.rxNumber && (
                  <p className="text-xs text-destructive">
                    {errors.rxNumber.message}
                  </p>
                )}
                {duplicateRxWarning && !errors.rxNumber && (
                  <div className="flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2.5">
                    <input
                      type="checkbox"
                      id="confirmDuplicate"
                      checked={confirmDuplicateRx}
                      onChange={(e) => setConfirmDuplicateRx(e.target.checked)}
                      className="h-4 w-4 rounded border-amber-400 mt-0.5 shrink-0 accent-amber-600"
                    />
                    <label
                      htmlFor="confirmDuplicate"
                      className="text-xs leading-relaxed text-amber-800"
                    >
                      This RX has been used before. This is common when multiple
                      NDCs are needed for one prescription. Check to confirm and
                      proceed.
                    </label>
                  </div>
                )}
              </div>
            </fieldset>

            <div className="border-t border-border/50" />

            {/* ── Section: Patient ───────────────────────── */}
            <fieldset className="space-y-3">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <UserCircle className="h-3.5 w-3.5" />
                Patient
                {requirePatientEnabled ? (
                  <span className="font-normal normal-case tracking-normal text-destructive">
                    — required
                  </span>
                ) : (
                  <span className="font-normal normal-case tracking-normal text-muted-foreground/60">
                    — optional
                  </span>
                )}
              </div>
              {linkedPatient ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <PatientCombobox
                      value={linkedPatient.id}
                      onSelect={(patient) => {
                        setLinkedPatient(patient);
                      }}
                      displayLabel={
                        linkedPatient.first_name
                          ? `${linkedPatient.first_name}${linkedPatient.last_name ? " " + linkedPatient.last_name : ""}`
                          : "Selected patient — click to change"
                      }
                      disabled={isSubmitting}
                      onCreateClick={(searchText) => {
                        setCreatePatientName(searchText);
                        setShowCreatePatient(true);
                      }}
                      refreshTrigger={patientsRefreshTrigger}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={() => setLinkedPatient(null)}
                    disabled={isSubmitting}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <PatientCombobox
                  onSelect={(patient) => {
                    setLinkedPatient(patient);
                  }}
                  placeholder="Search or create a patient..."
                  disabled={isSubmitting}
                  onCreateClick={(searchText) => {
                    setCreatePatientName(searchText);
                    setShowCreatePatient(true);
                  }}
                  refreshTrigger={patientsRefreshTrigger}
                />
              )}
            </fieldset>

            {/* ── Section: Note (optional, create mode only) ─────────── */}
            {mode === "create" && (
              <>
                <div className="border-t border-border/50" />
                <fieldset className="space-y-3">
                  <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    <StickyNote className="h-3.5 w-3.5" />
                    Note
                    <span className="font-normal normal-case tracking-normal text-muted-foreground/60">
                      — optional
                    </span>
                  </div>
                  <Textarea
                    id="note"
                    placeholder="Special instructions, follow-up details..."
                    className="min-h-[72px] resize-y text-sm"
                    maxLength={1000}
                    {...register("note")}
                  />
                  {errors.note && (
                    <p className="text-xs text-destructive">
                      {errors.note.message}
                    </p>
                  )}
                </fieldset>
              </>
            )}
          </form>
        )}
      </SidePanel>

      <PatientManagementModal
        open={showCreatePatient}
        onOpenChange={(o) => {
          setShowCreatePatient(o);
          if (!o) setCreatePatientName(undefined);
        }}
        defaultName={createPatientName}
        onPatientsChange={() => setPatientsRefreshTrigger((prev) => prev + 1)}
        onCreated={(newPatient) => {
          setLinkedPatient(newPatient);
          setCreatePatientName(undefined);
        }}
      />
    </>
  );
}
